/* eslint-disable complexity */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import {
  FormTextInput,
  FormCheckbox,
  FormTextarea,
  FormSelect,
  FormDatePicker,
  FormRadioGroup,
  FormFileUpload
} from "protocol-setup/components/FormComponents";
import {
  FormButtonGroup,
  FormButtonGroupHeader
} from "protocol-setup/components/FormComponents/FormButtonGroup";
import Text from "protocol-setup/components/Text";
import text from "protocol-setup/constants/text";
import {
  OpenSuccessNotification,
  OpenErrorNotification
} from "protocol-setup/components/notification";
import { usePSRSbyProtocolNumber } from "protocol-setup/redux/useLovs";
import {
  getCountryLabels,
  getOhrpCategoriesDefault,
  getSelectedCountryIds,
  getUserFriendlyError,
  log,
  mapDefaultValues,
  parseBoolean
} from "protocol-setup/helpers";
import {
  deleteFilePath,
  regulatorySetupPath
} from "protocol-setup/redux/constants";
import makeFileUploadRequest from "protocol-setup/redux/makeFileUploadRequest";
import { deleteWithAuth } from "protocol-setup/redux/makeFetch";
import { useRequestHistorySubmit } from "protocol-setup/helpers/withRequestHistory";

import PSRTable from "./PSR/PSRTable";
import regulatorySetupSchema from "./regulatorySetupSchema";
import {
  ohrpCategoriesOptions,
  indHolderOptions,
  levelIrbReviewOptions,
  whenExpeditedReviewAllowedOptions,
  ctsuCollectionRequiredOptions,
  collectIrbApprovalPostClosureOptions
} from "./Lovs";
import { constraintMappings } from "../OpenRave/orHelpers";
import { regTabNames } from "./regHelper";

export const onSubmit = async (
  data,
  onSuccessCallback,
  event,
  reqNum = "",
  countryList,
  selectedRequest
) => {
  event.preventDefault();
  const { attachPSR, ...d } = data;

  const formattedData = {
    ...d,
    requestNumber: reqNum
  };

  const upsertedCountries = selectedRequest?.countries || [];
  const countryArr = getSelectedCountryIds(
    d.countries,
    upsertedCountries,
    countryList
  );

  formattedData.countries = countryArr;
  formattedData.psrDetails = data.psrDetails.map(
    ({ trackbyId, psrName1, ...rest }) => ({
      ...rest
    })
  );

  formattedData.ohrpCategory = formattedData?.ohrpCategory?.join(",");
  delete formattedData.requestType;

  const fd = new FormData();
  let filesToDelete = [];

  // Append the main data as JSON string
  fd.append("reqStr", JSON.stringify(formattedData));

  // Handle file uploads
  if (Array.isArray(attachPSR) && attachPSR.length > 0) {
    attachPSR.forEach((file) => {
      if (file instanceof Blob || file instanceof File) {
        const { name } = file;
        fd.append("files", file, name);
      }
    });
  }

  // Handle file deletions
  if (selectedRequest?.attachPSR?.length > 0) {
    const existingFiles = selectedRequest.attachPSR;
    const newFiles = attachPSR.map((f) => f.name);
    filesToDelete = existingFiles.filter((f) => !newFiles.includes(f.name));
    log(filesToDelete);
  }

  try {
    const response = await makeFileUploadRequest(regulatorySetupPath, fd);
    await Promise.allSettled(
      filesToDelete.map((f) => {
        return deleteWithAuth(deleteFilePath(f.id));
      })
    );
    const { psregId } = response;

    onSuccessCallback({ ...data, reqNum, psregId });
    OpenSuccessNotification({
      message: text.regulatory.common.successUpdate.message,
      autoHide: true
    });
    return true;
  } catch (error) {
    const errorResponse = error?.response?.data || {};
    log("Error response from server:", errorResponse);
    const message = getUserFriendlyError(errorResponse, constraintMappings);
    OpenErrorNotification({
      message,
      autoHide: true
    });
    return false;
  }
};

const PSRItem = React.memo(({ item, handlePsrFieldChange }) => (
  <div className="row mb-2 align-items-center">
    {Object.keys(item).map((field) => (
      <div key={field} className="col">
        <input
          type={field === "modifiedDate" ? "date" : "text"}
          className="form-control"
          value={item[field]}
          onChange={(e) => handlePsrFieldChange(item.id, field, e.target.value)}
        />
      </div>
    ))}
  </div>
));
PSRItem.displayName = "PSRItem";

export function RegulatorySetupFormCore({
  defaultValues,
  requestNumber,
  countryList = [],
  psrList,
  isReqTypeAmendment,
  isReqTypeNewSubmission,
  selectedRequest,
  hasCompletedRequest,
  isSubmitFormEnabled,
  fetchPsData,
  isFormReadOnly,
  setDirtyTab
}) {
  const {
    handleSubmit,
    watch,
    reset,
    setValue,
    control,
    trigger,
    formState: { isDirty, dirtyFields }
  } = useForm({
    resolver: yupResolver(regulatorySetupSchema),
    defaultValues,
    shouldUnregister: false
  });

  useEffect(() => {
    trigger("ohrpCategory");
  }, [trigger]);

  const [showCtaFields, setShowCtaFields] = useState(false);
  const [requireOhrpCategories, setRequireOhrpCategories] = useState(false);
  const [requireIndAgentAndNumber, setRequireIndAgentAndNumber] = useState(false);
  const [isOhrpCategoryDisabled, setIsOhrpCategoryDisabled] = useState(false);

  useEffect(() => {
    setDirtyTab(isDirty);
  }, [isDirty]);

  const handleFormWatch = useCallback(
    (value, { name, type }) => {
      // Avoid processing on blur/focus events, only on change
      if (type !== "change") return;

      const updates = {};
      const fieldsToTrigger = [];
      // Canada-related fields
      if (name === "countries" || !name) {
        const isCanada = value.countries.includes("Canada");
        setShowCtaFields(isCanada);

        if (
          !isCanada &&
          (value.isCtaRequired !== null || value.ctaRequiredInfo !== null)
        ) {
          updates.isCtaRequired = false;
          updates.ctaRequiredInfo = "";
          fieldsToTrigger.push("isCtaRequired", "ctaRequiredInfo");
        }
      }

      // OHRP Categories
      if (name === "levelIrbReviewRequired" || !name) {
        const isExpedited = value.levelIrbReviewRequired === "Expedited";
        setRequireOhrpCategories(isExpedited);

        if (!isExpedited && value.ohrpCategory !== null) {
          updates.ohrpCategory = [];
          fieldsToTrigger.push("ohrpCategory");
        }
      }

      // IND Fields
      if (name === "indHolder" || !name) {
        const isNonCtepHeld = value.indHolder === "Non-CTEP Held IND";
        setRequireIndAgentAndNumber(isNonCtepHeld);

        if (
          !isNonCtepHeld &&
          (value.indAgent !== null || value.indNumber !== null)
        ) {
          updates.indAgent = "";
          updates.indNumber = "";
          fieldsToTrigger.push("indAgent", "indNumber");
        }
      }

      if (value.requestType) {
        if (!isReqTypeAmendment && value.ctsuCollectionRequired !== null) {
          updates.ctsuCollectionRequired = "";
          fieldsToTrigger.push("ctsuCollectionRequired");
        }

        if (
          isReqTypeAmendment &&
          (value.anticipatedPrimaryCompletionDt !== null ||
            value.isCollectIrbApprovalPostClosure !== null)
        ) {
          updates.anticipatedPrimaryCompletionDt = "";
          updates.isCollectIrbApprovalPostClosure = "";
          fieldsToTrigger.push(
            "anticipatedPrimaryCompletionDt",
            "isCollectIrbApprovalPostClosure"
          );
        }
      }

      // Batch updates
      if (Object.keys(updates).length > 0) {
        Object.entries(updates).forEach(([field, val]) => {
          setValue(field, val, { shouldValidate: false });
        });
        if (fieldsToTrigger.length > 0) {
          trigger(fieldsToTrigger);
        }
      }
    },
    [
      isReqTypeAmendment,
      setValue,
      trigger,
      setShowCtaFields,
      setRequireOhrpCategories,
      setRequireIndAgentAndNumber
    ]
  );

  useEffect(() => {
    const subscription = watch(handleFormWatch);
    return () => subscription.unsubscribe();
  }, [watch, handleFormWatch]);

  const memoizedSelectedRequest = useMemo(
    () => selectedRequest,
    [selectedRequest]
  );
  const memoizedCountryList = useMemo(() => countryList, [countryList?.length]);

  useEffect(() => {
    if (memoizedSelectedRequest) {
      const newValues = mapDefaultValues(
        memoizedSelectedRequest,
        defaultValues,
        {
          ctsuCollectionRequired: parseBoolean(
            memoizedSelectedRequest.ctsuCollectionRequired
          ),
          ohrpCategory: memoizedSelectedRequest.ohrpCategory?.split(",") || [],
          countries: getCountryLabels(
            memoizedSelectedRequest.countries,
            memoizedCountryList
          )
        }
      );
      setIsOhrpCategoryDisabled(
        memoizedSelectedRequest.levelIrbReviewRequired === "Full"
      );
      reset(newValues);

      setShowCtaFields(newValues.countries?.includes("Canada"));
      setRequireOhrpCategories(newValues.levelIrbReviewRequired === "Expedited");
      setRequireIndAgentAndNumber(newValues.indHolder === "Non-CTEP Held IND");
    }
  }, [memoizedSelectedRequest, memoizedCountryList, reset]);

  const resetForm = () => reset(defaultValues);
  const onSuccess = async () => {
    await fetchPsData();
  };

  // Wrap onSubmit with request history tracking
  const onSubmitWithHistory = useRequestHistorySubmit(
    (data, onSuccessCallback, event) =>
      onSubmit(data, onSuccessCallback, event, requestNumber, countryList, selectedRequest),
    selectedRequest?.protocolVersionDt ? dirtyFields : { "initialSave": true },
    regTabNames.REGULATORY_SETUP,
    selectedRequest?.protocolVersionDt ? "Updates" : "Initial Save"
  );

  const formTitle = "Regulatory Setup";

  return (
    <div
      className={`form-layout-wrapper my-3 ${isFormReadOnly ? "read-only" : ""
        }`}
    >
      <form
        data-testid="form"
        onSubmit={handleSubmit((data, event) => {
          const d = {
            ...data,
            requestNumber: memoizedSelectedRequest.requestNumber,
            psrId: memoizedSelectedRequest.psrId
          }
          return onSubmitWithHistory(
            d,
            onSuccess,
            event
          )
        }
        )}
      >
        <div className="form-layout-header">
          {isFormReadOnly ? (
            <></>
          ) : (
            <FormButtonGroupHeader
              control={control}
              reset={resetForm}
              formTitle={formTitle}
            />
          )}
          <div className="form-layout-title invisible">Protocol Setup</div>
        </div>
        <div
          className={`form-layout-body ${isFormReadOnly ? "read-only" : ""
            }`}
        >
          <div className="invisible"></div>
          <ul>
            <li className="row form-sub-group-1 p-2 mb-3">
              <div className="col-md-4 mr-4">
                <FormDatePicker
                  name="protocolVersionDt"
                  section="regulatory"
                  form="regulatorySetupForm"
                  control={control}
                  trigger={trigger}
                  required
                  disabled={isFormReadOnly}
                />
              </div>
              <div className="col-md-4 mr-4">
                <FormTextInput
                  name="protocolVersionNumber"
                  section="regulatory"
                  form="regulatorySetupForm"
                  control={control}
                  trigger={trigger}
                  required
                  readOnly={isFormReadOnly}
                />
              </div>
              <div className="col-md-4"></div>
            </li>

            <li className="row form-sub-group-2 p-2 mb-3">
              <div className="col-md-4 mr-4">
                <FormRadioGroup
                  name="levelIrbReviewRequired"
                  section="regulatory"
                  form="regulatorySetupForm"
                  control={control}
                  onChangeCallBack={(value) => {
                    if (value === "Full") {
                      setValue("ohrpCategory", []);
                      setValue("whenExpeditedReviewAllowed", "");
                    } else {
                      setValue(
                        "ohrpCategory",
                        getOhrpCategoriesDefault(
                          selectedRequest.requestType,
                          hasCompletedRequest
                        )
                      );
                    }
                    trigger("whenExpeditedReviewAllowed");
                    trigger("ohrpCategory");
                  }}
                  options={levelIrbReviewOptions}
                  required
                  readOnly={isFormReadOnly}
                />
              </div>
              <div className="col-md-4 mr-4">
                {watch("levelIrbReviewRequired") === "Expedited" ? (
                  <FormSelect
                    name="ohrpCategory"
                    section="regulatory"
                    form="regulatorySetupForm"
                    control={control}
                    trigger={trigger}
                    options={ohrpCategoriesOptions}
                    required={requireOhrpCategories}
                    isMulti
                    disabled={isOhrpCategoryDisabled || isReqTypeAmendment}
                    readOnly={isFormReadOnly}
                    defaultValue={[]}
                  />
                ) : null}
              </div>
              <div className="col-md-4">
                {isReqTypeNewSubmission && watch("levelIrbReviewRequired") === "Expedited" ? (
                  <FormRadioGroup
                    name="whenExpeditedReviewAllowed"
                    section="regulatory"
                    form="regulatorySetupForm"
                    control={control}
                    trigger={trigger}
                    options={whenExpeditedReviewAllowedOptions}
                    required
                    readOnly={isFormReadOnly}
                  />
                ) : null}
              </div>
            </li>
            <li className="row form-sub-group-3 p-2 mb-3">
              <div className="col-md-4 mr-4">
                <FormDatePicker
                  name="releaseDt"
                  section="regulatory"
                  form="regulatorySetupForm"
                  control={control}
                  required
                  trigger={trigger}
                  pickDateTime
                  disabled={isFormReadOnly}
                />
              </div>
              <div className="col-md-4 mr-4">
                {isReqTypeAmendment && (
                  <FormSelect
                    name="ctsuCollectionRequired"
                    section="regulatory"
                    form="regulatorySetupForm"
                    control={control}
                    trigger={trigger}
                    options={ctsuCollectionRequiredOptions}
                    required
                    readOnly={isFormReadOnly}
                  />
                )}
              </div>
              <div className="col-md-4 mr-4">
                {isReqTypeAmendment && (
                  <FormDatePicker
                    name="requiredByDt"
                    section="regulatory"
                    form="regulatorySetupForm"
                    control={control}
                    required={watch("ctsuCollectionRequired") === true}
                    trigger={trigger}
                    pickDateTime
                    disabled={isFormReadOnly}
                  />
                )}
              </div>
            </li>
            {/* Group 4: Completion Date & Post-Closure Information */}
            {!isReqTypeAmendment && (
              <li className="row form-sub-group-4 p-2 mb-3">
                <div className="col-md-4 mr-4">
                  <FormDatePicker
                    name="anticipatedPrimaryCompletionDt"
                    section="regulatory"
                    form="regulatorySetupForm"
                    control={control}
                    trigger={trigger}
                    required
                    disabled={isFormReadOnly}
                  />
                </div>
                <div className="col-md-4">
                  <FormRadioGroup
                    name="isCollectIrbApprovalPostClosure"
                    section="regulatory"
                    form="regulatorySetupForm"
                    control={control}
                    trigger={trigger}
                    options={collectIrbApprovalPostClosureOptions}
                    required
                    readOnly={isFormReadOnly}
                  />
                </div>
                <div className="col-md-4"></div>
              </li>
            )}
            {/* Group 5: Participating Countries & CTA Information */}
            <li className="d-block form-sub-group">
              <div className="row">
                <div className="col-md-4 mr-4">
                  <FormSelect
                    name="countries"
                    section="regulatory"
                    form="regulatorySetupForm"
                    control={control}
                    trigger={trigger}
                    options={countryList}
                    required={isReqTypeNewSubmission}
                    isMulti
                    readOnly={isFormReadOnly}
                  />
                </div>
                <div className="col-md-4 mr-4">
                  {showCtaFields && (
                    <>
                      <div className="color-dark-blue mr-3">
                        <span className="form-layout-required">*</span>
                        <Text bundle="regulatory.regulatorySetupForm.isCtaRequired.checkboxLabel" />
                      </div>
                      <FormCheckbox
                        name="isCtaRequired"
                        section="regulatory"
                        form="regulatorySetupForm"
                        control={control}
                        className="my-1"
                        checked={
                          typeof watch("isCtaRequired") === "boolean" &&
                          watch("isCtaRequired")
                        }
                        handleOnclick={(e) => {
                          if (e.target.checked) {
                            setValue("ctaRequiredInfo", "");
                            setValue("isCtaRequired", true);
                          } else {
                            setValue("isCtaRequired", false);
                          }
                        }}
                        readOnly={isFormReadOnly}
                        wrapperClassName={isFormReadOnly ? 'read-only-show' : ''}
                        disabled={isFormReadOnly}
                      />
                      <FormTextInput
                        name="ctaRequiredInfo"
                        section="regulatory"
                        form="regulatorySetupForm"
                        hideLabel
                        control={control}
                        required={watch("isCtaRequired")}
                        trigger={trigger}
                        disabled={
                          !(
                            typeof watch("isCtaRequired") === "boolean" &&
                            watch("isCtaRequired")
                          )
                        }
                        readOnly={isFormReadOnly}
                      />
                    </>
                  )}
                </div>
                <div className="col-md-4"></div>
              </div>
              <div className="mt-3 col-md-12">
                <FormTextarea
                  name="participationChangeDesc"
                  section="regulatory"
                  form="regulatorySetupForm"
                  control={control}
                  rows={3}
                  trigger={trigger}
                  readOnly={isFormReadOnly}
                />
              </div>
            </li>
            {/* Group 6: IND Information */}
            {isReqTypeNewSubmission && (
              <li className="row form-sub-group">
                <div className="col-md-4 mr-4">
                  <FormTextInput
                    name="indAgent"
                    section="regulatory"
                    form="regulatorySetupForm"
                    control={control}
                    required={requireIndAgentAndNumber}
                    trigger={trigger}
                    readOnly={isFormReadOnly}
                  />
                </div>
                <div className="col-md-4 mr-4">
                  <FormSelect
                    name="indHolder"
                    section="regulatory"
                    form="regulatorySetupForm"
                    control={control}
                    trigger={trigger}
                    options={indHolderOptions}
                    required
                    readOnly={isFormReadOnly}
                  />
                </div>
                <div className="col-md-4">
                  <FormTextInput
                    name="indNumber"
                    section="regulatory"
                    form="regulatorySetupForm"
                    control={control}
                    required={requireIndAgentAndNumber}
                    trigger={trigger}
                    readOnly={isFormReadOnly}
                  />
                </div>
              </li>)}
            {/* Group 7: PSR Requirements */}
            <li className="row form-sub-group-7 p-2">
              <div className="col-md-12">
                <PSRTable
                  control={control}
                  psrList={psrList}
                  name="psrDetails"
                  trigger={trigger}
                  readOnly={isFormReadOnly}
                />
              </div>
            </li>
            <li className="row form-sub-group-8 p-2 mb-3">
              <div className="col-md-12">
                <FormFileUpload
                  name="attachPSR"
                  section="regulatory"
                  form="regulatorySetupForm"
                  control={control}
                  multiple
                  trigger={trigger}
                  disabled={isFormReadOnly}
                />
              </div>
            </li>
          </ul>
        </div>
        <div className="form-layout-footer">
          {isFormReadOnly ? (
            <></>
          ) : (
            <FormButtonGroup
              control={control}
              reset={resetForm}
              formTitle={formTitle}
              trigger={trigger}
              formPartType={selectedRequest?.formPartType}
              requestNumber={selectedRequest?.requestNumber}
              isSubmitFormEnabled={isSubmitFormEnabled}
              onSuccess={onSuccess}
            />
          )}
        </div>
      </form>
    </div>
  );
}

function RegulatorySetupForm({
  selectedRequest,
  requestNumber,
  countryList,
  hasCompletedRequest,
  isSubmitFormEnabled,
  setIsSubmitFormEnabled,
  fetchPsData,
  isFormReadOnly,
  setDirtyTab
}) {
  const protocolNumber = selectedRequest?.protocolNumber;

  const { psrList } = usePSRSbyProtocolNumber(protocolNumber);

  const fieldConfig = {
    protocolVersionDt: "",
    protocolVersionNumber: "",
    requestType: "",
    ohrpCategory: ['1a'],
    releaseDt: "",
    levelIrbReviewRequired: "",
    ctsuCollectionRequired: "",
    requiredByDt: "",
    anticipatedPrimaryCompletionDt: "",
    isCollectIrbApprovalPostClosure: "",
    isCtaRequired: false,
    countries: [],
    ctaRequiredInfo: "",
    participationChangeDesc: "",
    indAgent: "",
    indHolder: "",
    indNumber: "",
    psrDetails: [],
    attachPSR: [],
    psRegId: null,
    whenExpeditedReviewAllowed: ""
  };

  const defaultValues = mapDefaultValues(selectedRequest, fieldConfig, {
    requestNumber: selectedRequest?.requestNumber || requestNumber || "",
    ohrpCategory: selectedRequest?.ohrpCategory?.split(",").filter(Boolean) || [],
    countries: getCountryLabels(selectedRequest?.countries, countryList),
    ctsuCollectionRequired: parseBoolean(selectedRequest?.ctsuCollectionRequired)
  });

  if (selectedRequest?.requestType) {
    return (
      <RegulatorySetupFormCore
        defaultValues={defaultValues}
        requestNumber={requestNumber}
        psrList={psrList}
        countryList={countryList}
        isReqTypeAmendment={selectedRequest?.requestType === "AMENDMENT"}
        isReqTypeNewSubmission={selectedRequest?.requestType === "NEW_SUBMISSION"}
        selectedRequest={selectedRequest}
        hasCompletedRequest={hasCompletedRequest}
        isSubmitFormEnabled={isSubmitFormEnabled}
        fetchPsData={fetchPsData}
        setDirtyTab={setDirtyTab}
        isFormReadOnly={isFormReadOnly}
      />
    );
  }
  return null;
}

export default RegulatorySetupForm;
